/**
 * KioskDeliveryBoard - Standalone delivery whiteboard for Raspberry Pi
 * 
 * Features:
 * - No login required (uses kiosk access key in URL)
 * - Auto-refreshes every 60 seconds
 * - Shows weekly delivery schedule with color-coded notes
 * - Light theme optimized for large displays
 * 
 * Usage: /kiosk/delivery?key=YOUR_KIOSK_KEY
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  RefreshCw, Maximize2, Minimize2, AlertTriangle, Truck, Calendar,
  ChevronLeft, ChevronRight, Wifi, WifiOff, Package, Clock, Star, Info, MapPin, Store, Hourglass
} from "lucide-react";
import { isClickCollect, requestTypeLabel, doneLabel, formatWaitingSince, daysWaiting, isOverdueClickCollect, CC_ALERT_DAYS } from "../delivery/requestTypes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Note type definitions with colors (light theme)
const NOTE_TYPES = {
  general: { label: 'General', bg: 'bg-amber-100', border: 'border-amber-400', text: 'text-amber-800', icon: Info },
  urgent: { label: 'Urgent', bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-800', icon: AlertTriangle },
  collection: { label: 'Collection', bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-800', icon: Package },
  timed: { label: 'Timed', bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-800', icon: Clock },
  priority: { label: 'Priority', bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-800', icon: Star }
};

const KioskDeliveryBoard = () => {
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    return monday.toISOString().split('T')[0];
  });
  const [whiteboard, setWhiteboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);

  // Get kiosk key from URL
  const urlParams = new URLSearchParams(window.location.search);
  const kioskKey = urlParams.get('key') || '';

  // Overscan compensation for TVs that crop the edges (Raspberry Pi over HDMI).
  // Tune with ?overscan=N (percent per side, 0-10). Default 3%.
  const overscanParam = parseFloat(urlParams.get('overscan'));
  const overscan = Number.isFinite(overscanParam) ? Math.max(0, Math.min(10, overscanParam)) : 3;

  const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const DAY_LABELS = { 
    monday: 'Monday', 
    tuesday: 'Tuesday', 
    wednesday: 'Wednesday', 
    thursday: 'Thursday', 
    friday: 'Friday' 
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  // Auto-enter fullscreen on load
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    setTimeout(() => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    }, 1000);
    
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Navigate weeks
  const changeWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(current.toISOString().split('T')[0]);
  };

  // Fetch whiteboard data
  const fetchWhiteboard = useCallback(async () => {
    if (!kioskKey) {
      setError('Missing kiosk access key. Add ?key=YOUR_KEY to URL');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/kiosk/delivery-whiteboard/${weekStart}?key=${kioskKey}`);
      setWhiteboard(response.data.whiteboard || response.data);
      setIsConnected(true);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to fetch whiteboard:', err);
      setIsConnected(false);
      if (err.response?.status === 401) {
        setError('Invalid kiosk access key');
      } else if (err.response?.status === 404) {
        // No whiteboard for this week - show empty
        setWhiteboard({ days: {} });
      }
    } finally {
      setLoading(false);
    }
  }, [kioskKey, weekStart]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    fetchWhiteboard();
    const interval = setInterval(fetchWhiteboard, 60000);
    return () => clearInterval(interval);
  }, [fetchWhiteboard]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Get formatted week range
  const getWeekRange = () => {
    const start = new Date(weekStart);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 4);
    const opts = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString('en-IE', opts)} - ${end.toLocaleDateString('en-IE', opts)}`;
  };

  // Get date for a specific day
  const getDayDate = (dayIndex) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + dayIndex);
    return date.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' });
  };

  // Check if day is today
  const isToday = (dayIndex) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + dayIndex);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Click & Collect items waiting longer than CC_ALERT_DAYS (anywhere on the loaded week)
  const overdueCount = Object.values(whiteboard?.days || {})
    .flatMap((d) => d.jobs || [])
    .filter(isOverdueClickCollect).length;

  // Error screen
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8">
          <AlertTriangle className="w-20 h-20 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Kiosk Error</h1>
          <p className="text-xl text-red-600 mb-6">{error}</p>
          <p className="text-gray-500">
            URL format: /kiosk/delivery?key=YOUR_KIOSK_KEY
          </p>
        </div>
      </div>
    );
  }

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-2xl font-bold text-gray-800">Loading Delivery Board...</p>
          <p className="text-gray-500 mt-2">Kiosk Mode</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen bg-gray-100 flex flex-col overflow-hidden"
      style={{ paddingTop: `${overscan}vh`, paddingBottom: `${overscan}vh`, paddingLeft: `${overscan}vw`, paddingRight: `${overscan}vw` }}
    >
      {/* Header - Light theme */}
      <header className="flex-shrink-0 px-4 py-3 bg-white border-b border-gray-300 shadow-sm flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Truck className="w-8 h-8 text-blue-600" />
          <h1 className="text-2xl font-black text-gray-800 tracking-tight">
            DELIVERY <span className="text-blue-600">WHITEBOARD</span>
          </h1>
          
          {/* Week Navigation */}
          <div className="flex items-center gap-2 ml-4">
            <button 
              onClick={() => changeWeek(-1)}
              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="text-gray-800 font-bold">{getWeekRange()}</span>
            </div>
            <button 
              onClick={() => changeWeek(1)}
              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Connection indicator */}
          <div className={`flex items-center gap-1 px-2 py-1 rounded ${isConnected ? 'bg-green-100' : 'bg-red-100'}`}>
            {isConnected ? (
              <Wifi className="w-4 h-4 text-green-600" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-600 animate-pulse" />
            )}
            <span className={`text-xs font-bold ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
              {isConnected ? 'LIVE' : 'OFFLINE'}
            </span>
          </div>

          {/* Overdue Click & Collect alert */}
          {overdueCount > 0 && (
            <div
              className="cc-flash flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-red-600 shadow-lg"
              data-testid="cc-overdue-alert"
            >
              <Hourglass className="w-5 h-5" />
              <span className="text-lg font-black tabular-nums" data-testid="cc-overdue-count">{overdueCount}</span>
              <span className="text-sm font-bold uppercase tracking-wide">
                Click &amp; Collect waiting {CC_ALERT_DAYS}+ days
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={toggleFullscreen}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {isFullscreen ? (
              <Minimize2 className="w-5 h-5 text-gray-600" />
            ) : (
              <Maximize2 className="w-5 h-5 text-gray-600" />
            )}
          </button>
          <div className="text-right">
            <p className="text-3xl font-bold text-gray-800 font-mono">
              {currentTime.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
      </header>

      {/* Days Grid - Light theme */}
      <div className="flex-1 grid grid-cols-5 gap-3 p-3 overflow-hidden">
        {DAYS.map((day, dayIndex) => {
          const dayData = whiteboard?.days?.[day] || {};
          const jobs = dayData.jobs || [];
          const notes = dayData.noteEntries || [];
          const today = isToday(dayIndex);

          return (
            <div 
              key={day} 
              className={`flex flex-col rounded-xl overflow-hidden border-2 shadow-sm ${
                today ? 'border-blue-500 shadow-lg shadow-blue-200' : 'border-gray-300'
              }`}
            >
              {/* Day Header */}
              <div className={`px-4 py-3 ${today ? 'bg-blue-600' : 'bg-gray-700'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-black text-white uppercase tracking-wide">
                      {DAY_LABELS[day]}
                    </h2>
                    <p className={`text-sm ${today ? 'text-blue-100' : 'text-gray-300'}`}>
                      {getDayDate(dayIndex)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="bg-white/20 text-white text-sm font-bold px-2 py-1 rounded">
                      {jobs.length} jobs
                    </span>
                  </div>
                </div>
              </div>

              {/* Day Content - White background */}
              <div className="flex-1 bg-white overflow-y-auto">
                {/* Notes Section */}
                {notes.length > 0 && (
                  <div className="p-2 border-b border-gray-200">
                    <p className="text-xs text-gray-500 uppercase font-bold mb-2">Notes</p>
                    <div className="space-y-1">
                      {notes.map((note) => {
                        const noteType = note.type || 'general';
                        const typeConfig = NOTE_TYPES[noteType] || NOTE_TYPES.general;
                        const Icon = typeConfig.icon;
                        return (
                          <div 
                            key={note.id}
                            className={`${typeConfig.bg} ${typeConfig.border} border rounded px-2 py-1.5 flex items-start gap-2`}
                          >
                            <Icon className={`w-4 h-4 ${typeConfig.text} flex-shrink-0 mt-0.5`} />
                            <p className={`text-sm ${typeConfig.text} font-medium`}>{note.text}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Jobs Section */}
                <div className="p-2 space-y-2">
                  {jobs.length === 0 && notes.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      <Truck className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p>No deliveries scheduled</p>
                    </div>
                  )}
                  {jobs.map((job) => {
                    const reqType = (job.request_type || '').toLowerCase();
                    const isCollection = reqType === 'collection';
                    const isCC = isClickCollect(job);
                    const detail = job.notes || job.product || job.description || '';
                    const jobNo = job.job_id || job.order_number || '';
                    const hasRealNumber = jobNo && !String(jobNo).toUpperCase().startsWith('REQ-');
                    const cardClass = job.delivered
                      ? 'bg-green-50 border-green-300'
                      : isCC
                        ? 'bg-orange-100 border-orange-400 border-l-4'
                        : 'bg-gray-50 border-gray-200';
                    const numberClass = job.delivered ? 'text-green-700' : isCC ? 'text-orange-800' : 'text-blue-700';
                    const tagClass = isCC
                      ? 'bg-orange-500 text-white'
                      : isCollection ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700';
                    return (
                    <div 
                      key={job.job_id || job.id}
                      data-testid={`kiosk-job-${job.job_id || job.id}`}
                      className={`p-3 rounded-lg border ${cardClass}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`text-base font-bold ${numberClass}`}>
                              {hasRealNumber ? `#${jobNo}` : 'No Job #'}
                            </p>
                            {reqType && (
                              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${tagClass}`} data-testid={`kiosk-job-type-${job.job_id || job.id}`}>
                                {requestTypeLabel(reqType)}
                              </span>
                            )}
                            {job.time_slot && (
                              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                                {job.time_slot}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 break-words font-medium mt-0.5">
                            {job.customer || 'Customer'}
                          </p>
                          {isCC && job.pickup_point && (
                            <p className="text-sm text-orange-900 font-bold break-words flex items-start gap-1 mt-0.5" data-testid={`kiosk-job-pickup-${job.job_id || job.id}`}>
                              <Store className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                              {job.pickup_point}
                            </p>
                          )}
                          {job.location && (
                            <p className="text-sm text-gray-800 font-semibold break-words flex items-start gap-1 mt-0.5" data-testid={`kiosk-job-location-${job.job_id || job.id}`}>
                              <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                              <span>{job.location}</span>
                            </p>
                          )}
                          {detail && (
                            <p className="text-xs text-gray-500 break-words whitespace-pre-line" data-testid={`kiosk-job-detail-${job.job_id || job.id}`}>{detail}</p>
                          )}
                          {job.requested_by && (
                            <p className="text-[11px] text-gray-400 break-words">Requested by {job.requested_by}</p>
                          )}
                          {isCC && !job.delivered && job.carried_from && (
                            <p className={`mt-1 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded ${daysWaiting(job.carried_from) > CC_ALERT_DAYS ? 'bg-red-600 text-white animate-pulse' : 'bg-red-100 text-red-700'}`} data-testid={`kiosk-job-waiting-${job.job_id || job.id}`}>
                              <Hourglass className="w-3 h-3" /> Waiting since {formatWaitingSince(job.carried_from)} ({daysWaiting(job.carried_from)}d)
                            </p>
                          )}
                        </div>
                        {job.delivered && (
                          <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded flex-shrink-0">
                            {doneLabel(job)}
                          </span>
                        )}
                      </div>
                      {job.delivery_notes && (
                        <p className="mt-2 text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 border border-amber-200 break-words whitespace-pre-line">
                          {job.delivery_notes}
                        </p>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer - Light theme */}
      <footer className="flex-shrink-0 px-4 py-2 bg-white border-t border-gray-300 flex items-center justify-between">
        <p className="text-gray-500 text-sm">
          KIOSK MODE • Auto-refreshes every 60 seconds
        </p>
        {lastUpdate && (
          <p className="text-gray-500 text-sm">
            Last update: {lastUpdate.toLocaleTimeString()}
          </p>
        )}
      </footer>
    </div>
  );
};

export default KioskDeliveryBoard;
